import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useI18n } from "../I18nContext";
import PasswordInput from "../PasswordInput";
import ConsentChecks from "../ConsentChecks";

export default function ResetPassword() {
  const { token } = useParams();
  const { t, setLang } = useI18n();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [ready, setReady] = useState(false);
  const [screenLang, setScreenLang] = useState("en");
  const [needsConsent, setNeedsConsent] = useState(false);
  const [consent, setConsent] = useState({ terms: false, privacy: false, beta: false });
  const [legalVersions, setLegalVersions] = useState({ terms: "1.0", privacy: "1.0", beta: "1.0" });
  const allConsented = consent.terms && consent.privacy && consent.beta;

  useEffect(() => {
    let cancelled = false;
    api.get(`/auth/reset-password/${token}`)
      .then((r) => {
        const next = r.data?.lang || "en";
        if (cancelled) return;
        setScreenLang(next);
        setLang(next);
        setNeedsConsent(!!r.data?.needs_consent);
        setReady(true);
      })
      .catch(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, [token, setLang]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd !== pwd2) {
      setErr(t("auth.passwordsDontMatch"));
      return;
    }
    if (pwd.length < 6) {
      setErr(t("auth.passwordTooShort"));
      return;
    }
    if (needsConsent && !allConsented) {
      setErr(t("auth.consentRequired"));
      return;
    }
    setLoading(true);
    try {
      const payload = { token, new_password: pwd };
      if (needsConsent) {
        payload.consent_terms = consent.terms;
        payload.consent_privacy = consent.privacy;
        payload.consent_beta = consent.beta;
        payload.consent_versions = legalVersions;
      }
      await api.post("/auth/reset-password", payload);
      setLang(screenLang);
      setDone(true);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  if (!ready) {
    return (
      <div className="tb-app tb-auth-page">
        <Link to="/" className="tb-app-logo">Traction<span>Board</span></Link>
        <div className="tb-loading">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="tb-app tb-auth-page">
      <Link to="/" className="tb-app-logo">Traction<span>Board</span></Link>
      {done ? (
        <div className="tb-form" data-testid="reset-done">
          <h1 className="tb-h1" style={{ marginBottom: "0.5rem", fontSize: "1.4rem" }}>
            {t("auth.resetDoneTitle")}
          </h1>
          <p className="tb-muted">{t("auth.resetDoneBody")}</p>
          <Link to="/login" className="tb-primary-btn" data-testid="reset-go-login" style={{ display: "inline-block", marginTop: 12 }}>
            {t("auth.goToLogin")}
          </Link>
        </div>
      ) : (
        <form className="tb-form" onSubmit={submit} data-testid="reset-form">
          <h1 className="tb-h1" style={{ marginBottom: "0.5rem", fontSize: "1.4rem" }}>
            {t("auth.resetTitle")}
          </h1>
          <div>
            <label>{t("auth.newPassword")}</label>
            <PasswordInput value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={6} testid="reset-pwd" autoComplete="new-password" />
          </div>
          <div>
            <label>{t("auth.confirmPassword")}</label>
            <PasswordInput value={pwd2} onChange={(e) => setPwd2(e.target.value)} required minLength={6} testid="reset-pwd2" autoComplete="new-password" />
          </div>
          {needsConsent && (
            <ConsentChecks
              consent={consent}
              setConsent={setConsent}
              onVersions={setLegalVersions}
              testPrefix="reset-consent"
            />
          )}
          {err && <div className="tb-form-error" data-testid="reset-error">{err}</div>}
          <button type="submit" className="tb-primary-btn" disabled={loading || (needsConsent && !allConsented)} data-testid="reset-submit">
            {loading ? t("auth.loading") : t("auth.setNewPassword")}
          </button>
        </form>
      )}
    </div>
  );
}
